import { type FC, useEffect, useRef, useState } from "react";

import { InstanceAvatar } from "../InstanceAvatar";

interface MentionMenuMember {
  avatarSeed: string;
  displayName: string;
  id: string;
}

export const MentionMenu: FC<{
  members: MentionMenuMember[];
  onSelect: (displayName: string) => void;
}> = ({ members, onSelect }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      if (
        event.target instanceof Node &&
        containerRef.current?.contains(event.target)
      ) {
        return;
      }
      setOpen(false);
    }
    function handleKey(event: globalThis.KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Address a member"
        title="Address a member"
        onClick={() => setOpen((previous) => !previous)}
        className="flex h-9 items-center justify-center border border-slate-300 bg-white/60 px-3 font-mono text-[12px] font-bold text-emerald-700 transition-colors hover:border-emerald-400/40 focus:border-emerald-400/60 focus:bg-white focus:outline-none focus:ring-1 focus:ring-emerald-400/30 dark:border-slate-700/60 dark:bg-slate-900/60 dark:text-emerald-300 dark:focus:bg-slate-900"
      >
        @
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute bottom-full left-0 z-50 mb-1 max-h-48 w-max min-w-[160px] overflow-y-auto border border-emerald-400/30 bg-white/95 shadow-[4px_4px_0_rgba(15,23,42,0.18)] backdrop-blur-md dark:bg-slate-950/95 dark:shadow-[4px_4px_0_rgba(2,6,23,0.6)]"
        >
          <li className="border-b border-slate-200 px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-500 dark:border-slate-800/80">
            [ address ]
          </li>
          {members.map((member) => (
            <li key={member.id} role="option" aria-selected={false}>
              <button
                type="button"
                onClick={() => {
                  onSelect(member.displayName);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-2 py-2 text-left font-mono text-[11px] font-bold text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-emerald-700 dark:text-slate-300 dark:hover:bg-slate-800/70 dark:hover:text-emerald-200"
              >
                <InstanceAvatar seed={member.avatarSeed} size="sm" />
                <span className="truncate">@{member.displayName}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
